/**
 * Bridge to the shell app's Sentry instance.
 * The shell passes its sentry object (with load/log methods) via MFE config.
 * This module stores the reference so any part of the MFE can call sentry.log()
 * without importing or knowing about the shell's internals.
 *
 * @module sentryBridge
 */

let _sentry = null;

/**
 * Store the shell-provided sentry object.
 * @param {Object} sentry - Shell sentry object with load() and log() methods.
 */
export const setSentry = sentry => {
    _sentry = sentry;
};

/**
 * Capture an exception via the shell's Sentry integration.
 * No-ops gracefully when sentry is not available (standalone mode).
 * @param {Error} error - Error to capture.
 * @param {Object} [logData={}] - Extra context attached to the Sentry event.
 */
export const captureException = (error, logData = {}) => {
    try {
        if (_sentry?.log) {
            _sentry.log(error, logData);
        }
    } catch {
        // Sentry capture must never throw and break the app
    }
};

/**
 * Check whether sentry is available.
 * @returns {boolean} True if the shell provided a sentry object.
 */
export const isSentryAvailable = () => !!_sentry;
